import { Product as IProduct } from "../../api/commercial/generated-types";
import { Product } from "./product";
import { ProductRefType } from "./types";

export class ProductFamily {
  readonly name: IProduct["family"]; // any Product holds reference to family.
  readonly translationKey: string;
  private _products: Product[];

  constructor(
    name: IProduct["family"],
    products: IProduct[],
    productRefFromUrl?: ProductRefType,
    productFamilyFromUrl?: IProduct["family"],
  ) {
    this.name = name;
    this.translationKey = ProductFamily.getTranslationKeyFromName(name);
    this._products = this.connectProductWithFamily(
      products,
      productRefFromUrl,
      productFamilyFromUrl,
    );
  }

  public getDefaultProduct(): Product {
    return this._products.find((product: Product): boolean => {
      return product.isDefaultForFamily;
    });
  }

  public getProduct(productRef: ProductRefType): Product | undefined {
    return this._products.find((product: Product): boolean => {
      return product.productRef === productRef;
    });
  }

  public getHiddenProducts(): Product[] | undefined {
    return this._products.filter((product: Product) => {
      return !product.shouldBeShown;
    });
  }

  public getAllAvailableProducts(): Product[] | undefined {
    return this._products.filter((product: Product) => {
      return product.isActive;
    });
  }

  public getAllAvailableAndShownProducts(): Product[] | undefined {
    return this._products.filter((product: Product) => {
      return product.isActive && product.shouldBeShown;
    });
  }

  public getProductCount({
    areHiddenProductsCounted = false,
  }: {
    areHiddenProductsCounted: boolean;
  }): number {
    if (areHiddenProductsCounted) {
      return this._products.length;
    }

    const onlyShownProducts = this._products.filter(
      (product: Product): boolean => {
        return product.shouldBeShown;
      },
    );

    return onlyShownProducts.length;
  }

  private static getTranslationKeyFromName(name: IProduct["family"]): string {
    return `ProductFamily.${name}`;
  }

  private connectProductWithFamily(
    products: IProduct[],
    productFromUrl?: ProductRefType,
    productFamilyFromUrl?: IProduct["family"],
  ): Product[] {
    return products.map((product: IProduct) => {
      return new Product(
        product.productRef.value as ProductRefType,
        this,
        product.features,
        product.isDefaultForFamily,
        product.currency,
        product.bySalesLink,
        product.isActive,
        productFromUrl,
        productFamilyFromUrl,
      );
    });
  }

  /**
   * This getter returns all products related for this family. Disregarding any filtering.
   */
  get allProducts(): Product[] {
    return this._products;
  }

  /**
   * This getter returns all products for this family. With filtering applied. (shouldBeShown is checked)
   */
  get products(): Product[] {
    return this._products.filter((product: Product) => product.shouldBeShown);
  }

  set products(givenProducts: Product[]) {
    this._products = givenProducts;
  }

  get shouldBeShown(): boolean {
    return this._products
      .map((product: Product) => product.shouldBeShown)
      .reduce((acc, val) => acc || val, false);
  }

  syncProductsVisibility(productForVisibilityUpdate: Product): void {
    if (productForVisibilityUpdate.bySalesLink) {
      this.allProducts.forEach((product: Product): void => {
        product.shouldBeShown = true;
      });
      return;
    }

    this.allProducts.forEach((product: Product): void => {
      if (product.bySalesLink) {
        product.shouldBeShown = false;
      }
    });
  }

  static getProductFamilyColor(selectedProductFamily: ProductFamily) {
    if (selectedProductFamily.name === "SWISS_SME") {
      return "red";
    }

    return "blue";
  }
}
